import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  DueLoanItem,
  LoanAccountInfo,
  RepaymentListFilters,
  RepaymentPreviewResponse,
  RepaymentFormData,
  RepaymentResponse,
  RepaymentAllocation,
} from '../types';

interface LoanRepaymentState {
  // ── Due loans list (main dashboard) ──
  dueLoans: DueLoanItem[];
  filters: RepaymentListFilters;
  currentPage: number;
  pageSize: number;
  totalRecords: number;
  loadingDueLoans: boolean;

  // ── Officers & products for filter dropdowns ──
  officers: string[];

  // ── Selected loan & repayment form ──
  selectedLoan: DueLoanItem | LoanAccountInfo | null;
  repaymentBalance: RepaymentPreviewResponse | null;
  formData: RepaymentFormData;
  allocationPreview: RepaymentAllocation | null;
  showOtherFees: boolean;

  // ── Modal/form UI state ──
  repaymentModalOpen: boolean;
  loadingBalance: boolean;
  submitting: boolean;

  // ── Result / receipt ──
  repaymentResult: RepaymentResponse | null;
  showReceipt: boolean;

  // ── Actions ──
  setFilters: (filters: Partial<RepaymentListFilters>) => void;
  clearFilters: () => void;
  setCurrentPage: (page: number) => void;
  setPageSize: (size: number) => void;
  setDueLoans: (loans: DueLoanItem[], total: number) => void;
  setLoadingDueLoans: (loading: boolean) => void;
  setOfficers: (officers: string[]) => void;
  setSelectedLoan: (loan: DueLoanItem | LoanAccountInfo | null) => void;
  setRepaymentBalance: (balance: RepaymentPreviewResponse | null) => void;
  updateFormData: (data: Partial<RepaymentFormData>) => void;
  setAllocationPreview: (allocation: RepaymentAllocation | null) => void;
  setShowOtherFees: (show: boolean) => void;
  setRepaymentModalOpen: (open: boolean) => void;
  setLoadingBalance: (loading: boolean) => void;
  setSubmitting: (submitting: boolean) => void;
  setRepaymentResult: (result: RepaymentResponse | null) => void;
  setShowReceipt: (show: boolean) => void;
  resetForm: () => void;
}

const initialFormData: RepaymentFormData = {
  totalAmount: 0,
  applicationFeeAmount: 0,
  processingFeeAmount: 0,
  commissionFeeAmount: 0,
  commitmentFeeAmount: 0,
  penaltyAmount: 0,
  interestAmount: 0,
  principalAmount: 0,
};

const defaultFilters: RepaymentListFilters = {};

export const useLoanRepaymentStore = create<LoanRepaymentState>()(
  devtools(
    (set) => ({
      dueLoans: [],
      filters: { ...defaultFilters },
      currentPage: 0,
      pageSize: 20,
      totalRecords: 0,
      loadingDueLoans: false,
      officers: [],
      selectedLoan: null,
      repaymentBalance: null,
      formData: { ...initialFormData },
      allocationPreview: null,
      showOtherFees: false,
      repaymentModalOpen: false,
      loadingBalance: false,
      submitting: false,
      repaymentResult: null,
      showReceipt: false,

      setFilters: (f) => set((s) => ({ filters: { ...s.filters, ...f }, currentPage: 0 })),
      clearFilters: () => set((s) => ({ filters: { branch: s.filters.branch }, currentPage: 0 })),
      setCurrentPage: (page) => set({ currentPage: page }),
      setPageSize: (size) => set({ pageSize: size, currentPage: 0 }),
      setDueLoans: (loans, total) => set({ dueLoans: loans, totalRecords: total }),
      setLoadingDueLoans: (loading) => set({ loadingDueLoans: loading }),
      setOfficers: (officers) => set({ officers }),
      setSelectedLoan: (loan) => set({ selectedLoan: loan }),
      setRepaymentBalance: (balance) => set({ repaymentBalance: balance }),
      updateFormData: (data) =>
        set((state) => ({ formData: { ...state.formData, ...data } })),
      setAllocationPreview: (allocation) => set({ allocationPreview: allocation }),
      setShowOtherFees: (show) => set({ showOtherFees: show }),
      setRepaymentModalOpen: (open) => set({ repaymentModalOpen: open }),
      setLoadingBalance: (loading) => set({ loadingBalance: loading }),
      setSubmitting: (submitting) => set({ submitting }),
      setRepaymentResult: (result) => set({ repaymentResult: result }),
      setShowReceipt: (show) => set({ showReceipt: show }),

      resetForm: () =>
        set({
          selectedLoan: null,
          repaymentBalance: null,
          formData: { ...initialFormData },
          allocationPreview: null,
          repaymentResult: null,
          showReceipt: false,
          showOtherFees: false,
          repaymentModalOpen: false,
        }),
    }),
    { name: 'loan-repayment' }
  )
);
